import { createApi } from "@reduxjs/toolkit/query/react";
import { BasicEntityListingPayload } from "../types/Admin";
import { ListingResponse } from "../types/Listing";
import {
  CreateDashboardPayload,
  CustomWidget,
  DashboardItem,
  TCustomWidgetPayload,
  TEditCustomWidgetPayload,
  UpdateDashboardPayload,
} from "../types/Overview";
import { appBaseQuery, baseQueryWithAuthHandling } from "../utils/utils";
import { API_METHODS } from "./constants";
import { API_ENDPOINTS } from "./endpoints";
import { handleAuthErrorCode } from "./errorCodes";

export enum OverviewApiTags {
  DASHBOARD_CREATED = "dashboardCreated",
  DASHBOARD_MODIFIED = "dashboardModified",
  DASHBOARD_DELETED = "dashboardDeleted",
  CUSTOM_WIDGET_CREATED = "customWidgetCreated",
  CUSTOM_WIDGET_UPDATED = "customWidgetUpdated",
  CUSTOM_WIDGET_DELETED = "customWidgetDeleted",
}

export const reducerPath = "overviewApi";

export const OverviewAPIs = createApi({
  reducerPath,
  tagTypes: [
    OverviewApiTags.DASHBOARD_CREATED,
    OverviewApiTags.DASHBOARD_MODIFIED,
    OverviewApiTags.DASHBOARD_DELETED,
    OverviewApiTags.CUSTOM_WIDGET_CREATED,
    OverviewApiTags.CUSTOM_WIDGET_UPDATED,
    OverviewApiTags.CUSTOM_WIDGET_DELETED,
  ],
  baseQuery: (args, api, extraOptions) =>
    baseQueryWithAuthHandling(
      appBaseQuery.withAuthJson,
      args,
      api,
      extraOptions
    ),
  endpoints: (builder) => ({
    // Get list of dashboards
    getDashboardsList: builder.query<
      ListingResponse<DashboardItem[]>,
      BasicEntityListingPayload
    >({
      query: (params) => {
        return {
          url: API_ENDPOINTS.OVERVIEW.DASHBOARDS,
          method: API_METHODS.GET,
          params,
        };
      },
      providesTags: [
        OverviewApiTags.DASHBOARD_CREATED,
        OverviewApiTags.DASHBOARD_MODIFIED,
        OverviewApiTags.DASHBOARD_DELETED,
        OverviewApiTags.CUSTOM_WIDGET_UPDATED,
        OverviewApiTags.CUSTOM_WIDGET_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<DashboardItem[]>) => {
        return response;
      },
    }),

    createDashboard: builder.mutation<DashboardItem, CreateDashboardPayload>({
      query: (body) => {
        return {
          url: API_ENDPOINTS.OVERVIEW.DASHBOARDS,
          method: API_METHODS.POST,
          body,
        };
      },
      invalidatesTags: [OverviewApiTags.DASHBOARD_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: DashboardItem) => {
        return response;
      },
    }),

    updateDashboard: builder.mutation<DashboardItem, UpdateDashboardPayload>({
      query: ({ id, ...body }) => {
        return {
          url: API_ENDPOINTS.OVERVIEW.UPDATE_DELETE_DASHBOARD(id),
          method: API_METHODS.PATCH,
          body,
        };
      },
      // invalidatesTags: [OverviewApiTags.DASHBOARD_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: DashboardItem) => {
        return response;
      },
    }),

    // saveMovedDashboardWidget: builder.mutation<DashboardItem, UpdateDashboardPayload>({
    //   query: ({ id, ...body }) => {
    //     return {
    //       url: API_ENDPOINTS.OVERVIEW.UPDATE_DELETE_DASHBOARD(id),
    //       method: API_METHODS.PATCH,
    //       body,
    //     }
    //   },
    //   // invalidatesTags: [OverviewApiTags.DASHBOARD_MODIFIED], // disabled on purpose to prevent refetching of dashboard list
    //   transformErrorResponse(baseQueryReturnValue) {
    //     handleAuthErrorCode(baseQueryReturnValue);
    //     return baseQueryReturnValue;
    //   },
    //   transformResponse: (response: DashboardItem) => {
    //     return response;
    //   },
    // }),

    setDefaultDashboard: builder.mutation<DashboardItem, number>({
      query: (id) => {
        return {
          url: API_ENDPOINTS.OVERVIEW.SET_DEFAULT_DASHBOARD(id),
          method: API_METHODS.POST,
        };
      },
      invalidatesTags: [OverviewApiTags.DASHBOARD_MODIFIED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: DashboardItem) => {
        return response;
      },
    }),

    deleteDashboard: builder.mutation<DashboardItem, number>({
      query: (id) => {
        return {
          url: API_ENDPOINTS.OVERVIEW.UPDATE_DELETE_DASHBOARD(id),
          method: API_METHODS.DELETE,
        };
      },
      invalidatesTags: [OverviewApiTags.DASHBOARD_DELETED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: DashboardItem) => {
        return response;
      },
    }),

    createCustomWidget: builder.mutation({
      query: (params: TCustomWidgetPayload) => {
        return {
          url: API_ENDPOINTS.OVERVIEW.CUSTOM_WIDGET,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [OverviewApiTags.CUSTOM_WIDGET_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
    getCustomWidgetList: builder.query<ListingResponse<CustomWidget[]>, void>({
      query: () => {
        return {
          url: API_ENDPOINTS.OVERVIEW.CUSTOM_WIDGET,
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        OverviewApiTags.CUSTOM_WIDGET_CREATED,
        OverviewApiTags.CUSTOM_WIDGET_UPDATED,
        OverviewApiTags.CUSTOM_WIDGET_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<CustomWidget[]>) => {
        return response;
      },
    }),
    editCustomWidget: builder.mutation({
      query: ({ widget_id, ...params }: TEditCustomWidgetPayload) => {
        return {
          url: API_ENDPOINTS.OVERVIEW.SINGLE_CUSTOM_WIDGET(widget_id),
          method: API_METHODS.PATCH,
          body: params,
        };
      },
      invalidatesTags: [OverviewApiTags.CUSTOM_WIDGET_UPDATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
    deleteCustomWidget: builder.mutation({
      query: (widgetId: number) => {
        return {
          url: API_ENDPOINTS.OVERVIEW.SINGLE_CUSTOM_WIDGET(widgetId),
          method: API_METHODS.DELETE,
        };
      },
      invalidatesTags: [OverviewApiTags.CUSTOM_WIDGET_DELETED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
  }),
});

export const {
  useGetDashboardsListQuery,
  useCreateDashboardMutation,
  useUpdateDashboardMutation,
  // useSaveMovedDashboardWidgetMutation,
  useSetDefaultDashboardMutation,
  useDeleteDashboardMutation,
  useCreateCustomWidgetMutation,
  useGetCustomWidgetListQuery,
  useEditCustomWidgetMutation,
  useDeleteCustomWidgetMutation,
} = OverviewAPIs;
